/**
 * Client Profile Forms — modal form components for full CRUD on the
 * Certifications, Key Personnel, and Past Performance cards.
 *
 * v3.11.27 — brings these 3 cards to parity with the v3.11.26
 * Winning Proposals card (Add / Edit / Delete via modal). Schemas mirror
 * the backend Pydantic schemas in /app/backend/app/schemas/knowledge_base.py.
 *
 * Each form returns the saved record to the parent so it can refresh
 * its list without an extra round-trip.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Award, Users, Briefcase } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Enum option lists — kept in sync with /app/backend/app/schemas/knowledge_base.py
const CERT_CATEGORIES = ['QUALITY', 'SECURITY', 'SMALL_BUSINESS', 'TECHNICAL', 'OTHER'];
const CERT_STATUSES = ['ACTIVE', 'EXPIRED', 'PENDING_RENEWAL'];
const CLEARANCE_LEVELS = ['NONE', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET', 'TS_SCI'];
const PERSONNEL_AVAILABILITY = ['AVAILABLE', 'FULL_TIME', 'PART_TIME', 'ON_CONTRACT'];
const CONTRACT_TYPES = ['FFP', 'T_AND_M', 'CPFF', 'IDIQ', 'BPA', 'OTHER'];
const CPARS_RATINGS = [
  'EXCEPTIONAL',
  'VERY_GOOD',
  'SATISFACTORY',
  'MARGINAL',
  'UNSATISFACTORY',
  'NOT_RATED',
];

/**
 * Strip empty optional values so we don't push blank strings to optional
 * server fields (the backend tolerates `null` for any optional field but
 * rejects empty strings on validators like `min_length`).
 */
function cleanPayload(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

async function saveRecord({ url, method, body, authToken }) {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.detail || 'Save failed');
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Certification Form
// ---------------------------------------------------------------------------
export function CertificationFormModal({
  open,
  onOpenChange,
  organizationId,
  initial,
  onSaved,
}) {
  const { authToken } = useAuth();
  const editing = Boolean(initial?.id);
  const buildForm = () => ({
    name: initial?.name || '',
    issuing_body: initial?.issuing_body || '',
    certificate_number: initial?.certificate_number || '',
    category: initial?.category || 'QUALITY',
    status: initial?.status || 'ACTIVE',
    issue_date: initial?.issue_date || '',
    expiration_date: initial?.expiration_date || '',
    scope: initial?.scope || '',
    notes: initial?.notes || '',
  });
  const [form, setForm] = useState(buildForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, open]);

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Certification name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = cleanPayload(form);
      const data = await saveRecord({
        url: editing
          ? `${API_URL}/api/knowledge-base/certifications/${initial.id}`
          : `${API_URL}/api/knowledge-base/certifications?organization_id=${organizationId}`,
        method: editing ? 'PUT' : 'POST',
        body: payload,
        authToken,
      });
      toast.success(editing ? 'Certification updated' : 'Certification added');
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="certification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Award className="w-5 h-5 text-amber-600" />
            {editing ? 'Edit Certification' : 'Add Certification'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
          <div>
            <Label htmlFor="cert-name">Name *</Label>
            <Input
              id="cert-name"
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. ISO 9001:2015"
              data-testid="cert-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CERT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CERT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Issuing Body</Label>
              <Input value={form.issuing_body || ''} onChange={(e) => setForm({ ...form, issuing_body: e.target.value })} placeholder="ISO" />
            </div>
            <div>
              <Label>Certificate Number</Label>
              <Input value={form.certificate_number || ''} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} />
            </div>
            <div>
              <Label>Issue Date</Label>
              <Input type="date" value={form.issue_date || ''} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div>
              <Label>Expiration Date</Label>
              <Input type="date" value={form.expiration_date || ''} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Scope</Label>
            <Input value={form.scope || ''} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="What this certification covers" />
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-certification-btn">
            {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Key Personnel Form
// ---------------------------------------------------------------------------
export function KeyPersonnelFormModal({
  open,
  onOpenChange,
  organizationId,
  initial,
  onSaved,
}) {
  const { authToken } = useAuth();
  const editing = Boolean(initial?.id);
  const buildForm = () => ({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    title: initial?.title || '',
    labor_category: initial?.labor_category || '',
    clearance_level: initial?.clearance_level || 'NONE',
    clearance_expiration: initial?.clearance_expiration || '',
    years_experience: initial?.years_experience ?? '',
    availability: initial?.availability || 'AVAILABLE',
    current_assignment: initial?.current_assignment || '',
    hourly_rate: initial?.hourly_rate ?? '',
    annual_salary: initial?.annual_salary ?? '',
    bio: initial?.bio || '',
    // For brevity we keep arrays as comma-separated strings; normalize on save.
    professional_certifications_csv: (initial?.professional_certifications || []).join(', '),
    specializations_csv: (initial?.specializations || []).join(', '),
    technical_skills_csv: (initial?.technical_skills || []).join(', '),
  });
  const [form, setForm] = useState(buildForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, open]);

  const splitCsv = (s) =>
    (s || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error('First and last name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = cleanPayload({
        ...form,
        professional_certifications: splitCsv(form.professional_certifications_csv),
        specializations: splitCsv(form.specializations_csv),
        technical_skills: splitCsv(form.technical_skills_csv),
        years_experience: form.years_experience === '' ? undefined : Number(form.years_experience),
        hourly_rate: form.hourly_rate === '' ? undefined : Number(form.hourly_rate),
        annual_salary: form.annual_salary === '' ? undefined : Number(form.annual_salary),
      });
      // Strip the helper csv fields before send.
      delete payload.professional_certifications_csv;
      delete payload.specializations_csv;
      delete payload.technical_skills_csv;

      const data = await saveRecord({
        url: editing
          ? `${API_URL}/api/knowledge-base/key-personnel/${initial.id}`
          : `${API_URL}/api/knowledge-base/key-personnel?organization_id=${organizationId}`,
        method: editing ? 'PUT' : 'POST',
        body: payload,
        authToken,
      });
      toast.success(editing ? 'Personnel updated' : 'Personnel added');
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]" data-testid="key-personnel-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Users className="w-5 h-5 text-blue-600" />
            {editing ? 'Edit Key Personnel' : 'Add Key Personnel'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First Name *</Label>
              <Input value={form.first_name || ''} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="kp-first-name-input" />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Sr. Program Manager" />
            </div>
            <div>
              <Label>Labor Category</Label>
              <Input value={form.labor_category || ''} onChange={(e) => setForm({ ...form, labor_category: e.target.value })} placeholder="PM-3" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>Clearance Level</Label>
              <Select value={form.clearance_level} onValueChange={(v) => setForm({ ...form, clearance_level: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLEARANCE_LEVELS.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Clearance Expiration</Label>
              <Input type="date" value={form.clearance_expiration || ''} onChange={(e) => setForm({ ...form, clearance_expiration: e.target.value })} />
            </div>
            <div>
              <Label>Years of Experience</Label>
              <Input type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} />
            </div>
            <div>
              <Label>Availability</Label>
              <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERSONNEL_AVAILABILITY.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hourly Rate</Label>
              <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
            </div>
            <div>
              <Label>Annual Salary</Label>
              <Input type="number" value={form.annual_salary} onChange={(e) => setForm({ ...form, annual_salary: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Current Assignment</Label>
            <Input value={form.current_assignment || ''} onChange={(e) => setForm({ ...form, current_assignment: e.target.value })} />
          </div>
          <div>
            <Label>Professional Certifications (comma-separated)</Label>
            <Input value={form.professional_certifications_csv || ''} onChange={(e) => setForm({ ...form, professional_certifications_csv: e.target.value })} placeholder="PMP, ITIL v4, CISSP" />
          </div>
          <div>
            <Label>Specializations (comma-separated)</Label>
            <Input value={form.specializations_csv || ''} onChange={(e) => setForm({ ...form, specializations_csv: e.target.value })} placeholder="Cloud Migration, Zero Trust" />
          </div>
          <div>
            <Label>Technical Skills (comma-separated)</Label>
            <Input value={form.technical_skills_csv || ''} onChange={(e) => setForm({ ...form, technical_skills_csv: e.target.value })} placeholder="AWS, Python, Kubernetes" />
          </div>
          <div>
            <Label>Bio</Label>
            <Input value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-key-personnel-btn">
            {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Past Performance Form
// ---------------------------------------------------------------------------
export function PastPerformanceFormModal({
  open,
  onOpenChange,
  organizationId,
  initial,
  onSaved,
}) {
  const { authToken } = useAuth();
  const editing = Boolean(initial?.id);
  const buildForm = () => ({
    contract_title: initial?.contract_title || '',
    client_agency: initial?.client_agency || '',
    client_office: initial?.client_office || '',
    contract_number: initial?.contract_number || '',
    task_order_number: initial?.task_order_number || '',
    contract_type: initial?.contract_type || '',
    contract_value: initial?.contract_value ?? '',
    funded_value: initial?.funded_value ?? '',
    pop_start: initial?.pop_start || '',
    pop_end: initial?.pop_end || '',
    is_ongoing: !!initial?.is_ongoing,
    cpars_rating: initial?.cpars_rating || 'NOT_RATED',
    cpars_narrative: initial?.cpars_narrative || '',
    naics_codes_csv: (initial?.naics_codes || []).join(', '),
    psc_codes_csv: (initial?.psc_codes || []).join(', '),
    relevance_tags_csv: (initial?.relevance_tags || []).join(', '),
    description: initial?.description || '',
    key_accomplishments: initial?.key_accomplishments || '',
    poc_name: initial?.poc_name || '',
    poc_title: initial?.poc_title || '',
    poc_email: initial?.poc_email || '',
    poc_phone: initial?.poc_phone || '',
    prime_contractor: initial?.prime_contractor || '',
    role_on_contract: initial?.role_on_contract || '',
  });
  const [form, setForm] = useState(buildForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, open]);

  const splitCsv = (s) =>
    (s || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (!form.contract_title?.trim() || !form.client_agency?.trim()) {
      toast.error('Contract title and client agency are required');
      return;
    }
    setSaving(true);
    try {
      const payload = cleanPayload({
        ...form,
        naics_codes: splitCsv(form.naics_codes_csv),
        psc_codes: splitCsv(form.psc_codes_csv),
        relevance_tags: splitCsv(form.relevance_tags_csv),
        contract_value: form.contract_value === '' ? undefined : Number(form.contract_value),
        funded_value: form.funded_value === '' ? undefined : Number(form.funded_value),
      });
      delete payload.naics_codes_csv;
      delete payload.psc_codes_csv;
      delete payload.relevance_tags_csv;

      const data = await saveRecord({
        url: editing
          ? `${API_URL}/api/knowledge-base/past-performances/${initial.id}`
          : `${API_URL}/api/knowledge-base/past-performances?organization_id=${organizationId}`,
        method: editing ? 'PUT' : 'POST',
        body: payload,
        authToken,
      });
      toast.success(editing ? 'Past performance updated' : 'Past performance added');
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px]" data-testid="past-performance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Briefcase className="w-5 h-5 text-emerald-600" />
            {editing ? 'Edit Past Performance' : 'Add Past Performance'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Contract Title *</Label>
            <Input value={form.contract_title || ''} onChange={(e) => setForm({ ...form, contract_title: e.target.value })} data-testid="pp-contract-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client Agency *</Label>
              <Input value={form.client_agency || ''} onChange={(e) => setForm({ ...form, client_agency: e.target.value })} placeholder="Department of Defense" />
            </div>
            <div>
              <Label>Client Office</Label>
              <Input value={form.client_office || ''} onChange={(e) => setForm({ ...form, client_office: e.target.value })} placeholder="DISA / J6" />
            </div>
            <div>
              <Label>Contract Number</Label>
              <Input value={form.contract_number || ''} onChange={(e) => setForm({ ...form, contract_number: e.target.value })} />
            </div>
            <div>
              <Label>Task Order Number</Label>
              <Input value={form.task_order_number || ''} onChange={(e) => setForm({ ...form, task_order_number: e.target.value })} />
            </div>
            <div>
              <Label>Contract Type</Label>
              <Select value={form.contract_type || ''} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {CONTRACT_TYPES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CPARS Rating</Label>
              <Select value={form.cpars_rating} onValueChange={(v) => setForm({ ...form, cpars_rating: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CPARS_RATINGS.map((r) => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contract Value (USD)</Label>
              <Input type="number" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} />
            </div>
            <div>
              <Label>Funded Value (USD)</Label>
              <Input type="number" value={form.funded_value} onChange={(e) => setForm({ ...form, funded_value: e.target.value })} />
            </div>
            <div>
              <Label>Period of Performance — Start</Label>
              <Input type="date" value={form.pop_start || ''} onChange={(e) => setForm({ ...form, pop_start: e.target.value })} />
            </div>
            <div>
              <Label>Period of Performance — End</Label>
              <Input type="date" value={form.pop_end || ''} onChange={(e) => setForm({ ...form, pop_end: e.target.value })} disabled={form.is_ongoing} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="pp-is-ongoing"
              checked={form.is_ongoing}
              onChange={(e) => setForm({ ...form, is_ongoing: e.target.checked })}
            />
            <Label htmlFor="pp-is-ongoing" className="cursor-pointer">Currently active (no end date)</Label>
          </div>
          <div>
            <Label>NAICS Codes (comma-separated)</Label>
            <Input value={form.naics_codes_csv || ''} onChange={(e) => setForm({ ...form, naics_codes_csv: e.target.value })} placeholder="541512, 541519" />
          </div>
          <div>
            <Label>PSC Codes (comma-separated)</Label>
            <Input value={form.psc_codes_csv || ''} onChange={(e) => setForm({ ...form, psc_codes_csv: e.target.value })} placeholder="D302, D310" />
          </div>
          <div>
            <Label>Relevance Tags (comma-separated)</Label>
            <Input value={form.relevance_tags_csv || ''} onChange={(e) => setForm({ ...form, relevance_tags_csv: e.target.value })} placeholder="Cloud, Cyber, Zero Trust" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>Key Accomplishments</Label>
            <Input value={form.key_accomplishments || ''} onChange={(e) => setForm({ ...form, key_accomplishments: e.target.value })} />
          </div>
          <div>
            <Label>CPARS Narrative</Label>
            <Input value={form.cpars_narrative || ''} onChange={(e) => setForm({ ...form, cpars_narrative: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>POC Name</Label>
              <Input value={form.poc_name || ''} onChange={(e) => setForm({ ...form, poc_name: e.target.value })} />
            </div>
            <div>
              <Label>POC Title</Label>
              <Input value={form.poc_title || ''} onChange={(e) => setForm({ ...form, poc_title: e.target.value })} />
            </div>
            <div>
              <Label>POC Email</Label>
              <Input type="email" value={form.poc_email || ''} onChange={(e) => setForm({ ...form, poc_email: e.target.value })} />
            </div>
            <div>
              <Label>POC Phone</Label>
              <Input value={form.poc_phone || ''} onChange={(e) => setForm({ ...form, poc_phone: e.target.value })} />
            </div>
            <div>
              <Label>Prime Contractor</Label>
              <Input value={form.prime_contractor || ''} onChange={(e) => setForm({ ...form, prime_contractor: e.target.value })} placeholder="Self / Lockheed Martin / etc." />
            </div>
            <div>
              <Label>Role on Contract</Label>
              <Input value={form.role_on_contract || ''} onChange={(e) => setForm({ ...form, role_on_contract: e.target.value })} placeholder="Prime / Sub" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-past-performance-btn">
            {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
